<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense>
        <v-btn icon small @click="$router.back()"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <v-toolbar-title>
          라운드 상세
          <span v-if="roundInfo" class="text-caption grey--text ml-2">#{{ roundInfo.round_number }}</span>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip v-if="roundInfo" small :color="roundStatusColor(roundInfo.status)" dark>
          {{ roundStatusLabel(roundInfo.status) }}
        </v-chip>
      </v-toolbar>
    </v-card>

    <!-- 라운드 메타 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2" v-if="roundInfo">
      <v-card-title class="py-2 text-subtitle-1 font-weight-bold">
        <v-icon left small>mdi-information-outline</v-icon>라운드 정보
      </v-card-title>
      <v-divider></v-divider>
      <v-simple-table dense>
        <tbody>
          <tr>
            <td class="font-weight-bold" style="width:18%">게임</td>
            <td style="width:32%">{{ roundInfo.game_title }} <span class="text-caption grey--text ml-1">({{ roundInfo.template_code }})</span></td>
            <td class="font-weight-bold" style="width:18%">종목</td>
            <td>{{ roundInfo.symbol_name || '-' }} <span class="text-caption grey--text ml-1">{{ roundInfo.symbol }}</span></td>
          </tr>
          <tr>
            <td class="font-weight-bold">판돈</td>
            <td>{{ roundInfo.bet_amount ? Number(roundInfo.bet_amount).toLocaleString() + '원' : '-' }}</td>
            <td class="font-weight-bold">마진 / 지급</td>
            <td>{{ marginDisplay }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">시작 시각</td>
            <td>{{ formatKST(roundInfo.started_at) }}</td>
            <td class="font-weight-bold">베팅 마감</td>
            <td>{{ formatKST(roundInfo.bet_closed_at) }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">종료 시각</td>
            <td>{{ formatKST(roundInfo.ended_at) }}</td>
            <td class="font-weight-bold">정산 시각</td>
            <td>{{ formatKST(roundInfo.settled_at) }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">시작가</td>
            <td>{{ formatPrice(roundInfo.start_price) }}</td>
            <td class="font-weight-bold">종료가</td>
            <td>{{ formatPrice(roundInfo.end_price) }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">결과</td>
            <td colspan="3">
              <v-chip v-if="roundInfo.result_direction" x-small :color="resultColor(roundInfo.result_direction)" dark>
                {{ resultLabel(roundInfo.result_direction) }}
              </v-chip>
              <span v-else class="grey--text">-</span>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- 집계 요약 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2" v-if="summary">
      <v-card-title class="py-2 text-subtitle-1 font-weight-bold">
        <v-icon left small>mdi-chart-pie</v-icon>참여 집계
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-3">
        <v-row dense>
          <v-col cols="3"><div class="text-caption grey--text">총 참여</div><div class="text-h6">{{ summary.total_count || 0 }}명</div></v-col>
          <v-col cols="3"><div class="text-caption blue--text">UP 참여</div><div class="text-h6 blue--text">{{ summary.up_count || 0 }}명</div><div class="text-caption grey--text">{{ formatAmt(summary.up_amount) }}</div></v-col>
          <v-col cols="3"><div class="text-caption deep-orange--text">DOWN 참여</div><div class="text-h6 deep-orange--text">{{ summary.down_count || 0 }}명</div><div class="text-caption grey--text">{{ formatAmt(summary.down_amount) }}</div></v-col>
          <v-col cols="3">
            <div class="text-caption grey--text">정산 상태</div>
            <div class="text-body-2">
              <span class="green--text">승 {{ summary.win_count || 0 }}</span>
              · <span class="red--text">패 {{ summary.lose_count || 0 }}</span>
              · <span class="grey--text">대기 {{ summary.pending_count || 0 }}</span>
            </div>
          </v-col>
        </v-row>

        <v-row dense class="mt-2" v-if="summary.commission_count">
          <v-col cols="3">
            <div class="text-caption purple--text">수령 커미션</div>
            <div class="text-h6 purple--text">{{ formatAmt(summary.commission_total) }}</div>
            <div class="text-caption grey--text">{{ summary.commission_count }}건 차감</div>
          </v-col>
          <v-col cols="3">
            <div class="text-caption grey--text">커미션 전 지급</div>
            <div class="text-body-1">{{ formatAmt(summary.original_payout_total) }}</div>
          </v-col>
          <v-col cols="3">
            <div class="text-caption grey--text">최종 지급</div>
            <div class="text-body-1">{{ formatAmt(summary.final_payout_total) }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 참여자 리스트 -->
    <v-card class="mx-auto mt-4 mb-12" max-width="95%" elevation="2">
      <v-card-title class="py-2">
        <v-icon left small>mdi-account-multiple</v-icon>
        <span class="text-subtitle-1 font-weight-bold">참여자</span>
        <v-chip small outlined class="ml-2">{{ totalBets }}</v-chip>
        <v-spacer></v-spacer>

        <v-text-field
          v-model="search.keyword"
          placeholder="유저 ID 검색"
          prepend-inner-icon="mdi-magnify"
          outlined dense hide-details
          style="max-width: 220px"
          @keyup.enter="loadBets(1)"
          class="mr-2"
        ></v-text-field>
        <v-select
          v-model="search.direction"
          :items="directionOptions"
          label="방향"
          outlined dense hide-details
          style="max-width: 120px"
          class="mr-2"
          @change="loadBets(1)"
        ></v-select>
        <v-select
          v-model="search.result"
          :items="resultOptions"
          label="결과"
          outlined dense hide-details
          style="max-width: 120px"
          class="mr-2"
          @change="loadBets(1)"
        ></v-select>
        <v-btn small color="primary" @click="loadBets(1)">
          <v-icon left small>mdi-magnify</v-icon>검색
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="betHeaders"
        :items="betList"
        :server-items-length="totalBets"
        :options.sync="tableOptions"
        :footer-props="{'items-per-page-options':[20, 50, 100]}"
        :loading="loading"
        class="elevation-0"
        dense
        @update:options="onTableOptionChange"
      >
        <template v-slot:item.direction="{ item }">
          <v-chip x-small :color="item.direction === 'up' ? 'blue' : 'deep-orange'" dark>
            {{ item.direction === 'up' ? 'UP' : 'DOWN' }}
          </v-chip>
        </template>
        <template v-slot:item.bet_amount="{ item }">{{ formatAmt(item.bet_amount) }}</template>
        <template v-slot:item.payout_amount="{ item }">{{ item.payout_amount > 0 ? formatAmt(item.payout_amount) : '-' }}</template>
        <template v-slot:item.bet_price="{ item }">{{ formatPrice(item.bet_price) }}</template>
        <template v-slot:item.result="{ item }">
          <v-chip v-if="item.result" x-small :color="resultBadgeColor(item.result)" dark>
            {{ resultBadgeLabel(item.result) }}
          </v-chip>
          <span v-else class="grey--text">대기</span>
        </template>
        <template v-slot:item.created_at="{ item }">{{ formatKST(item.created_at) }}</template>
        <template v-slot:item.settled_at="{ item }">{{ formatKST(item.settled_at) }}</template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { formatKST } from '../../util/dateutil';

export default {
  name: 'Danta_GameRoundDetail',
  data() {
    return {
      roundId: null,
      roundInfo: null,
      summary: null,
      betList: [],
      totalBets: 0,
      loading: false,

      search: {
        keyword: '',
        direction: null,
        result: null,
      },

      tableOptions: {
        page: 1,
        itemsPerPage: 20,
      },

      directionOptions: [
        { text: '전체', value: null },
        { text: 'UP', value: 'up' },
        { text: 'DOWN', value: 'down' },
      ],
      resultOptions: [
        { text: '전체', value: null },
        { text: '승', value: 'win' },
        { text: '패', value: 'lose' },
        { text: '무', value: 'draw' },
        { text: '대기', value: 'pending' },
      ],

      betHeaders: [
        { text: '베팅 ID', value: 'bet_id', width: '80px' },
        { text: '유저 ID', value: 'user_id', width: '160px' },
        { text: '매체', value: 'app_key', width: '180px' },
        { text: '방향', value: 'direction', width: '70px', align: 'center' },
        { text: '베팅금액', value: 'bet_amount', width: '100px', align: 'right' },
        { text: '베팅가', value: 'bet_price', width: '110px', align: 'right' },
        { text: '결과', value: 'result', width: '80px', align: 'center' },
        { text: '지급', value: 'payout_amount', width: '100px', align: 'right' },
        { text: '베팅 시각', value: 'created_at', width: '150px' },
        { text: '정산 시각', value: 'settled_at', width: '150px' },
      ],
    };
  },
  computed: {
    /** 마진 + 실 지급 배수 표기. 예: "5% (지급 1.95x)" */
    marginDisplay() {
      const m = Number(this.roundInfo?.margin);
      if (!Number.isFinite(m)) return '-';
      const pct = (m * 100).toFixed(2).replace(/\.?0+$/, '');
      const multiplier = (2 - m).toFixed(2);
      return `${pct}% (지급 ${multiplier}x)`;
    },
  },
  created() {
    this.roundId = this.$route.query.round_id;
    if (!this.roundId) {
      alert('round_id가 없습니다.');
      this.$router.back();
      return;
    }
    this.loadDetail();
    this.loadBets(1);
  },
  methods: {
    formatKST(v) { return v ? formatKST(v) : '-'; },
    formatAmt(v) { return v ? Number(v).toLocaleString() + '원' : '0원'; },
    formatPrice(v) { return v ? Number(v).toLocaleString() : '-'; },
    roundStatusColor(s) {
      const m = { waiting:'blue-grey', open:'cyan', closed:'orange', finished:'purple', settled:'green', cancelled:'red' };
      return m[s] || 'grey';
    },
    roundStatusLabel(s) {
      const m = { waiting:'대기', open:'베팅중', closed:'마감', finished:'판정중', settled:'정산완료', cancelled:'취소' };
      return m[s] || s;
    },
    resultColor(r) { return r === 'up' ? 'blue' : r === 'down' ? 'deep-orange' : 'grey'; },
    resultLabel(r) { return r === 'up' ? '상승' : r === 'down' ? '하락' : '동일'; },
    resultBadgeColor(r) { return r === 'win' ? 'green' : r === 'lose' ? 'red' : 'grey'; },
    resultBadgeLabel(r) { return r === 'win' ? '승' : r === 'lose' ? '패' : r === 'draw' ? '무' : r; },

    ...mapActions(['DANTA_GET_GAME_ROUND_DETAIL', 'DANTA_GET_ROUND_BETS']),

    async loadDetail() {
      try {
        const res = await this.DANTA_GET_GAME_ROUND_DETAIL({ round_id: this.roundId });
        if (res) this.roundInfo = res.round_info || null;
      } catch (e) {
        console.error('상세 조회 실패:', e);
      }
    },

    async loadBets(page) {
      this.loading = true;
      try {
        const res = await this.DANTA_GET_ROUND_BETS({
          round_id: this.roundId,
          keyword: this.search.keyword || undefined,
          direction: this.search.direction || undefined,
          result: this.search.result || undefined,
          page: page || this.tableOptions.page,
          page_size: this.tableOptions.itemsPerPage,
        });
        if (res) {
          this.betList = res.bet_list || [];
          this.totalBets = res.total || 0;
          this.summary = res.summary || null;
          this.tableOptions.page = res.page || 1;
        }
      } catch (e) {
        console.error('참여자 조회 실패:', e);
      }
      this.loading = false;
    },

    onTableOptionChange(opts) {
      // 페이지/개수 변경 시 서버 재요청
      if (opts.page !== this.tableOptions.page || opts.itemsPerPage !== this.tableOptions.itemsPerPage) {
        this.tableOptions = { ...this.tableOptions, page: opts.page, itemsPerPage: opts.itemsPerPage };
        this.loadBets(opts.page);
      }
    },
  },
};
</script>
